/**
 * EiqChatPanelAppSync — chat via AWS AppSync.
 *
 * Channel options by role:
 *   HOST / SPEAKER:
 *     - Everyone       (public broadcast)
 *     - Panelists only (host/speaker private)
 *     - Direct message to <someone>
 *   ATTENDEE:
 *     - Everyone       (public broadcast)
 *     - Direct message to <host or speaker>
 *
 * Attendees never see PANELISTS-channel messages — the server filters
 * them out in getHostChatHistory via userRole, and the hook re-filters
 * client-side via visibleMessages.
 */
import React, { useEffect, useRef, useState } from "react";
import { useHostChatAppSync, ChatChannel } from "../../hooks/useHostChatAppSync";
import type { EiqChatPanelProps } from "./eiq-chat-panel";
import "./eiq-chat-panel.css";

const formatChatTime = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

const EiqChatPanelAppSync: React.FC<EiqChatPanelProps> = ({
  webinarId,
  userId,
  userName,
  userEmail,
  role = "ATTENDEE",
  className,
  hideHeader,
}) => {
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement | null>(null);

  const isPrivileged = role === "HOST" || role === "SPEAKER";

  const {
    messages,
    isConnected,
    error,
    channel,
    setChannel,
    recipient,
    setRecipient,
    participants,
    sendMessage,
    clearError,
  } = useHostChatAppSync({
    webinarId,
    userId,
    userName,
    userEmail,
    role,
  });

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  // Clear error after 5s
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => clearError(), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  const handleSend = () => {
    const text = draft.trim();
    if (!text) return;
    sendMessage(text);
    setDraft("");
  };

  /**
   * Single dropdown handles both channel switch and DM recipient.
   *
   * Privileged roles see:
   *   ""              → EVERYONE
   *   "__panelists__" → PANELISTS private (host+speaker)
   *   "<id>"          → DIRECT to that participant
   *
   * Attendees see:
   *   "__panelists__" (default) → PANELISTS — visible to hosts/speakers
   *   "<id>"                    → DIRECT to a specific host/speaker
   * Attendees NEVER see "Everyone" — their messages never reach other
   * attendees. Backend also coerces EVERYONE → PANELISTS for ATTENDEE
   * sender as defense in depth.
   */
  const currentDropdownValue =
    channel === "EVERYONE"
      ? ""
      : channel === "PANELISTS"
      ? "__panelists__"
      : recipient?.id || "";

  const handleChannelChange = (value: string) => {
    if (!value) {
      // Only privileged roles reach "Everyone". Attendees never see
      // this option in the menu, so no defensive coercion here.
      setChannel("EVERYONE");
      setRecipient(null);
      return;
    }
    if (value === "__panelists__") {
      setChannel("PANELISTS");
      setRecipient(null);
      return;
    }
    const found = participants.find((p) => p.id === value);
    if (found) {
      setChannel("DIRECT");
      setRecipient(found);
    }
  };

  const composerPlaceholder = "Type message here ...";

  return (
    <div className={`eiq-chat-panel ${className || ""}`}>
      {!hideHeader && (
        <div className="eiq-chat-panel__header">
          <span>Chat</span>
        </div>
      )}
      {error && <div className="eiq-chat-panel__error">{error}</div>}
      <div className="eiq-chat-panel__messages" ref={listRef}>
        {messages.map((m) => {
          const isOwn =
            m.senderId === userId ||
            (!!userEmail && m.senderEmail === userEmail);
          const isDm = m.channel === "DIRECT";
          const isPanelists = m.channel === "PANELISTS";
          const bubbleClasses = [
            "eiq-chat-panel__msg",
            isOwn ? "eiq-chat-panel__msg--own" : "eiq-chat-panel__msg--other",
            isDm ? "eiq-chat-panel__msg--dm" : "",
            isPanelists ? "eiq-chat-panel__msg--panelists" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const tooltipTime = formatChatTime(m.createdAt);
          return (
            <div key={m.id} className={bubbleClasses}>
              <strong title={tooltipTime}>
                {isOwn ? "Me" : m.senderName}
                {isDm && isOwn && m.targetUserName && (
                  <> to {m.targetUserName}
                    <span className="eiq-chat-panel__dm-tag"> (Direct Message)</span>
                  </>
                )}
                {isDm && !isOwn && m.targetUserId === userId && (
                  <> to Me
                    <span className="eiq-chat-panel__dm-tag"> (Direct Message)</span>
                  </>
                )}
                {isPanelists && isOwn && <> to Panelists</>}
                {isPanelists && !isOwn && <> to Panelists</>}
              </strong>
              <span className="eiq-chat-panel__msg-bubble" title={tooltipTime}>
                {m.message}
              </span>
            </div>
          );
        })}
      </div>

      {/*
        Channel selector.
        Privileged: Everyone / Panelists / DM a specific user.
        Attendee:   Panelists (private to hosts+speakers) / DM a specific
                    host or speaker. Attendees NEVER see "Everyone".
      */}
      <div className="eiq-chat-panel__recipient">
        <label htmlFor="eiq-chat-channel">To:</label>
        <select
          id="eiq-chat-channel"
          value={currentDropdownValue}
          onChange={(e) => handleChannelChange(e.target.value)}
        >
          {isPrivileged && <option value="">Everyone</option>}
          <option value="__panelists__">
            {isPrivileged
              ? "Panelists (hosts + speakers)"
              : "Hosts and speakers (private)"}
          </option>
          {participants.length > 0 && (
            <optgroup label={isPrivileged ? "Direct message" : "Ask a specific host/speaker"}>
              {participants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.role ? ` (${p.role})` : ""}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>

      <div className="eiq-chat-panel__composer">
        <input
          type="text"
          placeholder={composerPlaceholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={!isConnected}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSend();
          }}
        />
      </div>
    </div>
  );
};

export default EiqChatPanelAppSync;
